import { useContext, useState, useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { t } from 'ttag'

import { PB40T, PG } from '@h2/components/colors'
import { Skeleton } from '@h2/components/loading/skeleton'
import { Flex } from '@h2/components/layout/flex'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Icon } from '@h2/components/display/icon'
import interestProfilerSvg from '@h2/assets/svg/iWantTo/source/interestProfiler.svg'
import budgetSvg from '@h2/assets/svg/iWantTo/source/budget.svg'
import upTheLadderSvg from '@h2/assets/svg/iWantTo/source/upTheLadder.svg'
import careerPathwaysSvg from '@h2/assets/svg/iWantTo/source/careerPathways.svg'
import skillsProfilerSvg from '@h2/assets/svg/iWantTo/source/skillsProfiler.svg'

import { Context } from '../../context'
import { Link } from '../../../link'

const colorRgx = /data-currentcolor="([#\w]+)"/
const imageMap = {
  '/budget/': {
    svg: budgetSvg,
    color: budgetSvg.match(colorRgx)[1]
  },
  '/ip/': {
    svg: interestProfilerSvg,
    color: interestProfilerSvg.match(colorRgx)[1]
  },
  '/ladder': {
    svg: upTheLadderSvg,
    color: upTheLadderSvg.match(colorRgx)[1]
  },
  '/clusters': {
    svg: careerPathwaysSvg,
    color: careerPathwaysSvg.match(colorRgx)[1]
  },
  '/careerhub/reports/summary': {
    svg: skillsProfilerSvg,
    color: skillsProfilerSvg.match(colorRgx)[1]
  }
}
const Wrap = styled(Flex)`
  position: relative;
`
const Pager = styled.nav`
  display: flex;
  position: absolute;
  top: 0;
  right: 0;
`
const Page = styled.a`
  width: 7px;
  height: 7px;
  border: 1px solid ${PB40T};
  border-radius: 50%;
  background-color: white;
  margin-right: 2px;

  &:last-child {
    margin-right: 0;
  }

  &.active {
    background-color: ${PB40T};
  }
`
const Slides = styled.nav`
  width: 100%;
  display: flex;
  overflow: hidden;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
`
const Slide = styled(Link)`
  flex: 0 0 100%;

  &:hover {
    text-decoration: none;
  }
`
const Article = styled.article`
  display: flex;
  align-items: center;

  figure {
    margin-right: 15px;
  }

  section {
    max-height: 75px;

    header {
      > h5:first-child {
        text-transform: uppercase;
        color: ${PG};
        font-size: 10px;
        margin: 0;
        line-height: normal;
      }

      > h5:last-child {
        font-size: 16px;
        margin: 0 0 6px 0;
      }
    }

    p {
      line-height: 12px;
    }
  }
`
const prefix = 'iwt-'
const Popular = () => {
  const slidesRef = useRef()
  const { userMenu, isMenuFetching } = useContext(Context)
  const [active, setActive] = useState(0)
  const [touchStartX, setTouchStartX] = useState(0)
  const handleOnClick = useCallback(evt => {
    const href = evt.target.getAttribute('href')
    const anchor = document.querySelector(href)

    evt.stopPropagation()
    evt.preventDefault()

    if (anchor) {
      setActive(parseInt(href.replace(`#${prefix}`, '')))
    }
  }, [])
  const handleTouchStart = useCallback(evt => {
    const touches = Array.from(evt.changedTouches)

    evt.preventDefault()

    if (Array.isArray(touches) && touches.length > 0) {
      setTouchStartX(touches[0].pageX)
    }
  }, [])
  const handleTouchEnd = useCallback(
    evt => {
      const touches = Array.from(evt.changedTouches)

      evt.preventDefault()

      if (Array.isArray(touches) && touches.length > 0) {
        const touchEndX = touches[0].pageX

        if (Math.abs(touchEndX - touchStartX) < 10) {
          return document.querySelector(`#${prefix}${active}`).click()
        }

        // Forward
        if (touchEndX < touchStartX) {
          const next = active + 1

          if (next < userMenu?.topFeatures.length) {
            setActive(next)
          }
        }

        // Backward
        if (touchEndX > touchStartX) {
          const prev = active - 1

          if (prev >= 0) {
            setActive(prev)
          }
        }
      }
    },
    [touchStartX, active, userMenu?.topFeatures.length]
  )

  useEffect(() => {
    const slidesRefCurrent = slidesRef.current

    if (slidesRefCurrent) {
      slidesRefCurrent.addEventListener('touchstart', handleTouchStart, {
        passive: false
      })
      slidesRefCurrent.addEventListener('touchend', handleTouchEnd, { passive: false })
    }

    return () => {
      if (slidesRefCurrent) {
        slidesRefCurrent.removeEventListener('touchstart', handleTouchStart, {
          passive: false
        })
        slidesRefCurrent.removeEventListener('touchend', handleTouchEnd, {
          passive: false
        })
      }
    }
  }, [handleTouchStart, handleTouchEnd])

  useEffect(() => {
    const slide = document.querySelector(`#${prefix}${active}`)

    if (slide) {
      slide.scrollIntoView()
    }
  }, [active])

  return (
    <Wrap>
      <Pager>
        {!isMenuFetching &&
          userMenu?.topFeatures.map((item, idx) => (
            <Page
              key={item.url}
              href={`#${prefix}${idx}`}
              className={idx === active ? 'active' : null}
              onClick={handleOnClick}
            />
          ))}
      </Pager>
      <Slides ref={slidesRef}>
        {isMenuFetching ? (
          <Skeleton height="75px" />
        ) : (
          userMenu?.topFeatures.map((item, idx) => (
            <Slide key={item.url} id={`${prefix}${idx}`} href={item.url}>
              <Article>
                <figure>
                  <Icon
                    color="white"
                    width="auto"
                    height="70px"
                    dangerouslySetGlyph={imageMap[item.url].svg}
                  />
                </figure>
                <section>
                  <header>
                    <Heading level="5">{t`Popular Items`}</Heading>
                    <Heading level="5">{item.title}</Heading>
                  </header>
                  {item.description && <Text size="small">{item.description}</Text>}
                </section>
              </Article>
            </Slide>
          ))
        )}
      </Slides>
    </Wrap>
  )
}

export { Popular, imageMap }
